import { useState, useEffect, useCallback, useRef } from "react";

const screenshots = [
  {
    url: "https://storage.readdy-site.link/project_files/79f427a1-17a2-4ae0-8a3d-065a9c6426de/7437a25c-fc33-42e2-8668-b9bda4cb5001_1.png?v=e6ed9ff9257158dac7dff365a2f7f614",
    label: "Dashboard Principal",
  },
  {
    url: "https://storage.readdy-site.link/project_files/79f427a1-17a2-4ae0-8a3d-065a9c6426de/0f249246-cda1-4070-97c9-4e85d7a89717_2.png?v=06954c4507c99d035ab2ad6f677d050b",
    label: "Generador IA",
  },
  {
    url: "https://storage.readdy-site.link/project_files/79f427a1-17a2-4ae0-8a3d-065a9c6426de/f17cbf04-2076-45c2-8536-7ed64e0a6334_3.png?v=041f97054821be080b0a2ff48d3a5499",
    label: "Scripts de WhatsApp",
  },
  {
    url: "https://storage.readdy-site.link/project_files/79f427a1-17a2-4ae0-8a3d-065a9c6426de/7b55c2be-cd71-4c1b-a994-a950503826fe_4.png?v=22ef776923bd82383d04dbeb6e0be9e1",
    label: "Demoledor de Objeciones",
  },
  {
    url: "https://storage.readdy-site.link/project_files/79f427a1-17a2-4ae0-8a3d-065a9c6426de/10bd08fe-2435-48dd-a8b9-ad61c2c2fb1b_5.png?v=46a985a64c51dfff2eda2c907eea9fa9",
    label: "Fábrica de Contenidos",
  },
  {
    url: "https://storage.readdy-site.link/project_files/79f427a1-17a2-4ae0-8a3d-065a9c6426de/6b964966-fe5c-4812-92a1-cfc2da989cb7_6.png?v=447828284a3f95bf105bbb6cc861440a",
    label: "Academia de Tráfico",
  },
  {
    url: "https://storage.readdy-site.link/project_files/79f427a1-17a2-4ae0-8a3d-065a9c6426de/aa4ec330-b1bd-4c96-858f-82e47585a69b_7.png?v=7738e631c66b69355ed2528c25450bde",
    label: "Bóveda de Recursos",
  },
];

export default function PlatformPreview() {
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<"left" | "right">("right");
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const touchStartX = useRef<number | null>(null);

  const goTo = useCallback(
    (index: number, dir: "left" | "right" = "right") => {
      if (isAnimating) return;
      setDirection(dir);
      setIsAnimating(true);
      setTimeout(() => {
        setCurrent(index);
        setIsAnimating(false);
      }, 300);
    },
    [isAnimating]
  );

  const next = useCallback(() => {
    const nextIndex = (current + 1) % screenshots.length;
    goTo(nextIndex, "right");
  }, [current, goTo]);

  const prev = useCallback(() => {
    const prevIndex = (current - 1 + screenshots.length) % screenshots.length;
    goTo(prevIndex, "left");
  }, [current, goTo]);

  useEffect(() => {
    autoPlayRef.current = setInterval(next, 3500);
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [next]);

  const pauseAutoPlay = () => {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
  };

  const resumeAutoPlay = () => {
    autoPlayRef.current = setInterval(next, 3500);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      if (diff > 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  return (
    <section className="py-16 md:py-24 relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#7C3AED]/10 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-flex items-center gap-2 bg-[#7C3AED]/20 border border-[#7C3AED]/40 text-[#a78bfa] text-xs font-semibold px-4 py-1.5 rounded-full mb-4">
            <i className="ri-eye-line"></i>
            Vista previa real de la plataforma
          </span>
          <h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight">
            Mira por dentro tu{" "}
            <span className="bg-gradient-to-r from-[#7C3AED] to-[#f97316] bg-clip-text text-transparent">
              Centro de Mando
            </span>
          </h2>
          <p className="text-white/50 mt-3 text-sm md:text-base max-w-xl mx-auto">
            No te vendemos humo. Esto es exactamente lo que verás al ingresar. Herramientas reales, resultados reales.
          </p>
        </div>

        {/* Carousel wrapper */}
        <div
          className="relative"
          onMouseEnter={pauseAutoPlay}
          onMouseLeave={resumeAutoPlay}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Main screen frame */}
          <div className="relative mx-auto max-w-4xl">
            {/* Browser chrome decoration */}
            <div className="bg-[#1a1f2e] rounded-t-xl border border-white/10 border-b-0 px-4 py-2.5 flex items-center gap-2">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500/70" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
                <div className="w-3 h-3 rounded-full bg-green-500/70" />
              </div>
              <div className="flex-1 mx-3">
                <div className="bg-white/5 border border-white/10 rounded-md px-3 py-1 flex items-center gap-2 max-w-xs mx-auto">
                  <i className="ri-lock-line text-white/30 text-xs"></i>
                  <span className="text-white/30 text-xs">app.negoia.pe</span>
                </div>
              </div>
              <span className="text-white/30 text-xs hidden sm:block">
                {screenshots[current].label}
              </span>
            </div>

            {/* Image container */}
            <div className="relative overflow-hidden rounded-b-xl border border-white/10 border-t-0 bg-[#0d1117]"
              style={{ aspectRatio: "16/9" }}>
              <img
                key={current}
                src={screenshots[current].url}
                alt={screenshots[current].label}
                className="w-full h-full object-cover object-top"
                style={{
                  opacity: isAnimating ? 0 : 1,
                  transform: isAnimating
                    ? `translateX(${direction === "right" ? "30px" : "-30px"})`
                    : "translateX(0)",
                  transition: "opacity 0.3s ease, transform 0.3s ease",
                }}
              />

              {/* Gradient overlay bottom */}
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#0B0F19]/60 to-transparent pointer-events-none" />

              {/* Nav arrows */}
              <button
                onClick={prev}
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-black/50 hover:bg-[#7C3AED]/70 border border-white/10 hover:border-[#7C3AED]/50 rounded-full transition-all duration-200 cursor-pointer backdrop-blur-sm"
              >
                <i className="ri-arrow-left-s-line text-white text-lg"></i>
              </button>
              <button
                onClick={next}
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-black/50 hover:bg-[#7C3AED]/70 border border-white/10 hover:border-[#7C3AED]/50 rounded-full transition-all duration-200 cursor-pointer backdrop-blur-sm"
              >
                <i className="ri-arrow-right-s-line text-white text-lg"></i>
              </button>
            </div>
          </div>

          {/* Dots + label */}
          <div className="flex flex-col items-center gap-3 mt-5">
            <div className="flex items-center gap-2">
              {screenshots.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i, i > current ? "right" : "left")}
                  className={`transition-all duration-300 rounded-full cursor-pointer ${
                    i === current
                      ? "w-6 h-2 bg-[#f97316]"
                      : "w-2 h-2 bg-white/20 hover:bg-white/40"
                  }`}
                />
              ))}
            </div>
            <p className="text-white/40 text-xs">
              {current + 1} / {screenshots.length} — {screenshots[current].label}
            </p>
          </div>

          {/* Thumbnail strip — hidden on very small screens */}
          <div className="hidden sm:flex gap-2 md:gap-3 mt-6 justify-center flex-wrap">
            {screenshots.map((s, i) => (
              <button
                key={i}
                onClick={() => goTo(i, i > current ? "right" : "left")}
                className={`relative rounded-lg overflow-hidden border-2 transition-all duration-200 cursor-pointer flex-shrink-0 ${
                  i === current
                    ? "border-[#f97316] scale-105"
                    : "border-white/10 hover:border-white/30 opacity-60 hover:opacity-90"
                }`}
                style={{ width: 80, height: 50 }}
              >
                <img
                  src={s.url}
                  alt={s.label}
                  className="w-full h-full object-cover object-top"
                />
              </button>
            ))}
          </div>
        </div>

        {/* Bottom trust line */}
        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8 mt-10">
          {[
            { icon: "ri-shield-check-line", text: "Acceso inmediato al comprar" },
            { icon: "ri-device-line", text: "Funciona en cualquier dispositivo" },
            { icon: "ri-lock-password-line", text: "Acceso privado por usuario" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-white/40 text-xs md:text-sm">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-[#7C3AED]`}></i>
              </div>
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
